
import React, { useRef, useState, useEffect } from 'react';
import { Plus, Clock, Edit2, Trash2, Scissors, Droplet, Wind, Sparkles, X, Save, Type, Euro, Image as ImageIcon, Package, Flower2, Palette, Zap, Heart, Loader } from 'lucide-react';
import { getImageUrl, API_BASE } from '../../config';

const ICON_MAP = {
  'Scissors': Scissors,
  'Droplet': Droplet,
  'Palette': Palette,
  'Zap': Zap,
  'Wind': Wind,
  'Sparkles': Sparkles,
  'Package': Package,
  'Heart': Heart,
  'Flower2': Flower2,
  'Plus': Plus
};

// --- Service Modal Component ---
const ServiceModal = ({ isOpen, onClose, initialService, onSave, t }) => {
  const [formData, setFormData] = useState({ 
    name_fr: '', name_en: '', name_pt: '',
    description_fr: '', description_en: '', description_pt: '',
    price: '', duration: 60, icon: 'Scissors' 
  });
  const [langTab, setLangTab] = useState('fr');

  useEffect(() => {
    if (initialService) {
      setFormData({
        id: initialService.id,
        name_fr: initialService.name_fr || '',
        name_en: initialService.name_en || initialService.name_fr || '',
        name_pt: initialService.name_pt || initialService.name_fr || '',
        description_fr: initialService.description_fr || '',
        description_en: initialService.description_en || initialService.description_fr || '',
        description_pt: initialService.description_pt || initialService.description_fr || '',
        price: initialService.price || '',
        duration: initialService.duration || 60,
        icon: initialService.icon || 'Scissors'
      });
    } else {
        setFormData({ 
            name_fr: '', name_en: '', name_pt: '',
            description_fr: '', description_en: '', description_pt: '',
            price: '', duration: 60, icon: 'Scissors' 
        });
    }
  }, [initialService, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md transform transition-all scale-100">
        <div className="flex justify-between items-center p-6 border-b border-gray-100">
          <h3 className="text-xl font-bold text-gray-800">
            {initialService?.id ? t.admin.services.edit : t.admin.services.add}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="h-6 w-6" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
            {['fr', 'en', 'pt'].map((l) => (
                <button
                    key={l}
                    type="button"
                    onClick={() => setLangTab(l)}
                    className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        langTab === l ? 'bg-white text-pink-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                    }`}
                >
                    {l === 'fr' ? '🇫🇷 FR' : l === 'en' ? '🇬🇧 EN' : '🇵🇹 PT'}
                </button>
            ))}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {t.admin.services.name} ({langTab.toUpperCase()})
            </label>
            <div className="relative">
              <Type className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <input
                type="text"
                required={langTab === 'fr'}
                value={formData[`name_${langTab}`] || ''}
                onChange={(e) => setFormData({...formData, [`name_${langTab}`]: e.target.value})}
                className="pl-10 w-full rounded-lg border-gray-300 border p-2.5 focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all outline-none"
                placeholder={t.admin.services.placeholders.name}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {t.admin.services.description} ({langTab.toUpperCase()})
            </label>
            <textarea
              required={langTab === 'fr'}
              value={formData[`description_${langTab}`] || ''}
              onChange={(e) => setFormData({...formData, [`description_${langTab}`]: e.target.value})}
              className="w-full rounded-lg border-gray-300 border p-3 focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all outline-none resize-none"
              rows="3"
              placeholder={t.admin.services.placeholders.description}
            />
          </div>

          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">{t.admin.services.price}</label>
              <div className="relative">
                <Euro className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <input
                  type="number"
                  step="0.01"
                  required
                  value={formData.price || ''}
                  onChange={(e) => setFormData({...formData, price: e.target.value})}
                  className="pl-10 w-full rounded-lg border-gray-300 border p-2.5 focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all outline-none"
                  placeholder={t.admin.services.placeholders.price}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">{t.admin.services.duration}</label>
              <div className="relative">
                <Clock className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <input
                  type="number"
                  step="15"
                  min="15"
                  required
                  value={formData.duration || 60}
                  onChange={(e) => setFormData({...formData, duration: parseInt(e.target.value) || 0})}
                  className="pl-10 w-full rounded-lg border-gray-300 border p-2.5 focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all outline-none"
                />
              </div>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">{t.admin.services.icon}</label>
            <select
              value={formData.icon || 'Scissors'}
              onChange={(e) => setFormData({...formData, icon: e.target.value})}
              className="w-full rounded-lg border-gray-300 border p-2.5 focus:ring-2 focus:ring-pink-500 focus:border-transparent bg-white transition-all outline-none"
            >
              <option value="Scissors">✂️ {t.admin.services.icons.scissors}</option>
              <option value="Droplet">💧 {t.admin.services.icons.droplet}</option>
              <option value="Palette">🎨 {t.admin.services.icons.palette}</option>
              <option value="Zap">⚡ {t.admin.services.icons.zap}</option>
              <option value="Wind">💨 {t.admin.services.icons.wind}</option>
              <option value="Sparkles">✨ {t.admin.services.icons.sparkles}</option>
              <option value="Package">📦 {t.admin.services.icons.pack}</option>
              <option value="Heart">❤️ {t.admin.services.icons.heart}</option>
              <option value="Flower2">🌸 {t.admin.services.icons.flower}</option>
              <option value="Plus">➕ {t.admin.services.icons.plus}</option>
            </select>
          </div>
          <div className="pt-2 flex space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
            >
              {t.admin.services.cancel}
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2.5 bg-pink-600 text-white rounded-lg hover:bg-pink-700 font-medium shadow-md hover:shadow-lg transition-all flex justify-center items-center"
            >
              <Save className="h-5 w-5 mr-2" />
              {t.admin.services.save}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// --- Category Upload Component ---
const CategoryUpload = ({ label, currentImage, onFileChange, onRemove, t }) => {
    const inputRef = useRef(null);
    const [dragActive, setDragActive] = useState(false);

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
        else if (e.type === "dragleave") setDragActive(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            onFileChange(e.dataTransfer.files[0]);
        }
    };

    return (
        <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">{label}</label>
            <div 
                className={`relative border-2 border-dashed rounded-lg p-4 text-center transition-colors cursor-pointer h-40 flex flex-col items-center justify-center
                    ${dragActive ? 'border-pink-500 bg-pink-50' : 'border-gray-300 hover:border-pink-400 hover:bg-gray-50'}
                `}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => inputRef.current?.click()}
            >
                <input
                    ref={inputRef}
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={(e) => {
                        if (e.target.files && e.target.files[0]) onFileChange(e.target.files[0]);
                    }}
                />
                
                {currentImage ? (
                    <div className="absolute inset-0 w-full h-full overflow-hidden rounded-lg group">
                        <img 
                            src={getImageUrl(currentImage)} 
                            alt={label} 
                            className="w-full h-full object-cover" 
                        />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                             <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onRemove();
                                }}
                                className="bg-white rounded-full p-2 text-red-600 hover:bg-red-50"
                            >
                                <Trash2 className="h-5 w-5" />
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        <ImageIcon className={`h-8 w-8 mb-2 ${dragActive ? 'text-pink-600' : 'text-gray-400'}`} />
                        <p className="text-xs text-gray-500">Ajouter</p>
                    </>
                )}
            </div>
        </div>
    );
};

export default function AdminServices({ services, t, onDelete, onSave, onRefresh }) {
    const [isEditingService, setIsEditingService] = useState(false);
    const [currentService, setCurrentService] = useState(null);
    const [isSavingImages, setIsSavingImages] = useState(false);

    // Images catégories
    const [categoryImages, setCategoryImages] = useState({
        haircut_image: '', coloring_image: '', balayage_image: '', lissage_image: '', styling_image: '', treatment_image: '', pack_image: '', massageDetente_image: '', massageSoins_image: '', extras_image: ''
    });
    const [categoryFiles, setCategoryFiles] = useState({});

    const loadSettings = async () => {
        try {
            const res = await fetch(`${API_BASE}/settings`);
            const data = await res.json();
            setCategoryImages({
                haircut_image: data.haircut_image || '',
                coloring_image: data.coloring_image || '',
                balayage_image: data.balayage_image || '',
                lissage_image: data.lissage_image || '',
                styling_image: data.styling_image || '',
                treatment_image: data.treatment_image || '',
                pack_image: data.pack_image || '',
                massageDetente_image: data.massageDetente_image || '',
                massageSoins_image: data.massageSoins_image || '',
                extras_image: data.extras_image || ''
            });
        } catch (err) {
            console.error("Erreur chargement images settings:", err);
        }
    };

    useEffect(() => {
        loadSettings();
    }, []);

    const handleOpenModal = (service) => {
        setCurrentService(service);
        setIsEditingService(true);
    };

    const handleSaveServiceWrapper = async (data) => {
        await onSave(data);
        setIsEditingService(false);
    };

    const handleCategoryFileChange = (file, key) => {
        setCategoryFiles(prev => ({ ...prev, [`${key}_file`]: file }));
        setCategoryImages(prev => ({ ...prev, [key]: URL.createObjectURL(file) }));
    };

    const handleSaveCategoryImages = async () => {
        const formData = new FormData();
        let hasChanges = false;
        
        ['haircut', 'coloring', 'balayage', 'lissage', 'styling', 'treatment', 'pack', 'massageDetente', 'massageSoins', 'extras'].forEach(key => {
            const fileKey = `${key}_image_file`;
            const imageKey = `${key}_image`;
            
            if (categoryFiles[fileKey]) {
                formData.append(fileKey, categoryFiles[fileKey]);
                hasChanges = true;
            } else if (categoryImages[imageKey] === '') {
                 formData.append(imageKey, ''); 
                 hasChanges = true;
            }
        });

        if (!hasChanges) return alert("Aucun changement à enregistrer.");

        setIsSavingImages(true);
        try {
            const response = await fetch(`${API_BASE}/settings`, { method: 'POST', body: formData });
            if (response.ok) {
                alert("Mise à jour réussie !");
                setCategoryFiles({});
                await loadSettings();
                if (onRefresh) onRefresh();
            } else {
                const error = await response.json();
                alert(`Erreur : ${error.error || 'Erreur inconnue'}`);
            }
        } catch (err) {
            console.error(err);
            alert("Erreur serveur.");
        } finally {
            setIsSavingImages(false);
        }
    };

    return (
        <div className="space-y-12">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                <div 
                    onClick={() => handleOpenModal(null)}
                    className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-pink-500 hover:bg-pink-50 transition-colors h-full min-h-[250px]"
                >
                    <Plus className="h-10 w-10 text-gray-400 mb-2" />
                    <span className="text-gray-600 font-medium">{t.admin.services.add}</span>
                </div>

                {services.map((service) => {
                    const name = service.name_fr || service.name_en || service.name_pt || 'Service';
                    const IconComponent = ICON_MAP[service.icon] || Scissors;

                    return (
                        <div key={service.id} className="bg-white rounded-lg shadow overflow-hidden flex flex-col h-full border border-gray-100 hover:shadow-md transition-shadow">
                            <div className="p-6 flex-grow">
                                <div className="flex justify-between items-start mb-4">
                                    <h3 className="text-xl font-bold text-gray-800">{name}</h3>
                                    <div className="bg-pink-100 p-2 rounded-lg">
                                        <IconComponent className="h-6 w-6 text-pink-600" />
                                    </div>
                                </div>
                                <p className="text-gray-600 mb-4 text-sm line-clamp-3">{service.description_fr || service.description_en}</p>
                                <div className="flex items-center text-sm text-gray-500 mb-2">
                                    <Clock className="h-4 w-4 mr-2" /> {service.duration} min
                                </div>
                                <div className="text-2xl font-bold text-pink-600">{service.price}€</div>
                            </div>
                            
                            <div className="bg-gray-50 px-6 py-3 border-t border-gray-100 flex justify-end space-x-3">
                                <button onClick={() => handleOpenModal(service)} className="flex items-center px-3 py-1.5 bg-white border border-gray-300 rounded text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors">
                                    <Edit2 className="h-4 w-4 mr-1.5" />
                                    {t.admin.common.edit}
                                </button>
                                <button onClick={() => onDelete(service.id)} className="flex items-center px-3 py-1.5 bg-white border border-gray-300 rounded text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors">
                                    <Trash2 className="h-4 w-4 mr-1.5" />
                                    {t.admin.common.delete}
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="bg-white rounded-lg shadow p-6 border border-gray-100">
                <h3 className="text-lg font-bold text-gray-800 mb-6 flex items-center">
                    <ImageIcon className="h-5 w-5 mr-2 text-pink-600" />
                    {t.admin.services.categoryImages}
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6">
                    {['haircut', 'coloring', 'balayage', 'lissage', 'styling', 'treatment', 'pack', 'massageDetente', 'massageSoins'].map(key => (
                         <CategoryUpload 
                            key={key}
                            label={t.rates?.categories?.[key] || key} 
                            currentImage={categoryImages[`${key}_image`]}
                            onFileChange={(f) => handleCategoryFileChange(f, `${key}_image`)}
                            onRemove={() => {
                                setCategoryImages(prev => ({...prev, [`${key}_image`]: ''}));
                                setCategoryFiles(prev => ({...prev, [`${key}_image_file`]: null}));
                            }}
                            t={t}
                        />
                    ))}
                </div>
                <div className="mt-6 flex justify-end">
                    <button 
                        onClick={handleSaveCategoryImages} 
                        disabled={isSavingImages}
                        className="px-6 py-2.5 bg-pink-600 text-white rounded-lg hover:bg-pink-700 font-medium shadow transition-colors flex items-center disabled:opacity-50"
                    >
                        {isSavingImages ? <Loader className="animate-spin h-4 w-4 mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                        {isSavingImages ? t.admin.common.loading : t.admin.services.saveImages}
                    </button>
                </div>
            </div>

            <ServiceModal 
                isOpen={isEditingService}
                onClose={() => setIsEditingService(false)}
                initialService={currentService}
                onSave={handleSaveServiceWrapper}
                t={t}
            />
        </div>
    );
}
